// === WOONHUIS SCREEN WITH CUSTOM FIELDS ===
// Generated by Sluitstraat Generator
// Module: jsonforms
//
// ProductService Calculation Feature:
// - Manual calculation via "Calculate Premium" button
// - Auto-calculation when fields with x-onChangeMethods change
// - Real-time feedback with success/error messages
// - Results display panel showing premium and coverage
// - Debug panel for technical request/response inspection

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  bffClient,
  getNextScreen,
  getPreviousScreen,
  getCurrentStepNumber,
  calculateTotalSteps,
  formatPrice,
  LoadingScreen,
  DatePicker,
  computeWoonhuisDisplayPremium,
  getConfigPathForBrand,
  getCurrentBrand,
} from '@sluitstraat/core';
import { woonhuisStyles as styles } from '../../styles/commonStyles';
import { getProductOrder } from '../../workflowConfig';
import { findSiblingRiskAddress, findUpstreamAddress, findUpstreamRiskAddress, shouldHideAddressSection, syncAddressToOtherProducts, syncRiskAddressToProperties, vpAddressAsBa } from '../../utils/addressSharing';
import { readFamilyComposition, findSharedFamilyComposition } from '../../utils/familyComposition';
import { useSharedFamilyComposition } from '../../hooks/useSharedFamilyComposition';
import { DebugPanel } from '../../components/DebugPanel';
import { useSharedRiskAddress } from '../../hooks/useSharedRiskAddress';
import { useDeferredCommit } from '../../hooks/useDeferredCommit';
import AddressSection, { Address } from '../../components/AddressSection';
import { WithHelpSidebar } from '../../components/HelpCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { WoningSpecificaties } from '../../components/WoningSpecificaties';
import { InfoTooltip } from '../../components/InfoTooltip';
import { House, ClipboardText, Users, Shield, CurrencyEur } from 'phosphor-react';
import { Check } from 'lucide-react';
import { stableStringify } from '../../utils/stableStringify';
import {
  mapToCalculationRequest,
  mapFromCalculationResponse,
  applyWoonhuisDiscountInPlace,
  cleanUnselectedCoveragePremiums,
  extractDefaults,
  deepMerge,
  formatCurrency,
  unformatCurrency,
  linkRelationData,
  linkInboedelAddress,
  validateWoonhuis,
  validateBuildingNumberField,
  scrollToFirstError,
  useCoverageCalculation,
  useInfofolio,
  PropertyWarning,
  applyWoonhuisFieldUpdate,
} from './woonhuis';

const SCREEN_ID = 'woonhuis';
const STORM_DEDUCTIBLE_OPTIONS = [
  { value: 'N', amount: 250 },
  { value: 'J', amount: 0 },
] as const;

export const WoonhuisScreen: React.FC = () => {


  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData, setRelationData } = useWorkflow();
  const { config, isLoading: isConfigLoading } = useConfig();
  // Servicevergoeding (PP_EKST) - config-driven via prolongationFee (vaste 2.50 per product)
  const ppEkstConfig = (config as any)?.prolongationFee;
  const ppEkst = ppEkstConfig?.enabled === false ? 0 : (ppEkstConfig?.amount ?? 2.5);

  // Shared address hook - single source of truth for the policyholder address
  const { address: sharedAddress, updateAddress: updateSharedAddress } = useSharedAddress();
  // Shared risk-address hook - separate slice + Ja/Nee answer, shared across property products
  const { riskAddressSame, riskAddressSource, updateRiskAddress, setRiskAddressSame } = useSharedRiskAddress();
  const { stage: stageAddressSave, schedule: scheduleAddressSave } = useDeferredCommit();

  const [data, setData] = useState<any>(() => getStepData(SCREEN_ID) || {});

  // Single source of truth for the family composition across every product.
  const { familyComposition, setFamilyComposition } = useSharedFamilyComposition();

  // Migrate a value entered before the shared store existed / on a since-deselected product.
  useEffect(() => {
    if (!familyComposition) {
      const found = findSharedFamilyComposition(getStepData);
      if (found) setFamilyComposition(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  // Mirror the shared value into OP_GSS so the BFF payload carries it.
  useEffect(() => {
    if (!familyComposition) return;
    setData((prev: any) => {
      if (prev?.Contractdocument?.PP?.OP?.OP_GSS === familyComposition) return prev;
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            OP: { ...prev?.Contractdocument?.PP?.OP, OP_GSS: familyComposition },
          },
        },
      };
      setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);
  const [, setSchema] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Risk address toggle - 'J' means insured property address = policyholder address.
  // Per-product RisicoAdresGelijk wins (BFF reads it); otherwise follow the shared answer.
  const [risicoAdresGelijk, setRisicoAdresGelijk] = useState(() => {
    return data?.Supportsection?.Divers?.RisicoAdresGelijk || riskAddressSame || 'J';
  });


  // Rented-property detection: BFF rejects inboedel when BA_WONING is 'D' (deels verhuurd) or 'H' (verhuurd).
  // Only surface the warning when inboedel is in the cart — woonhuis itself is unaffected.
  const isInboedelBlockedByRental = (() => {
    if (!selectedProducts?.inboedel) return false;
    const woning = data?.Contractdocument?.PP?.BA?.BA_WONING;
    return woning === 'D' || woning === 'H';
  })();

  // Mobile keyboard dismissal detection - triggers blur when keyboard closes
  // This fixes the issue where dismissing the keyboard doesn't trigger onBlur
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;

    let lastHeight = window.visualViewport.height;

    const handleViewportResize = () => {
      const currentHeight = window.visualViewport?.height || 0;
      // Keyboard dismissed when viewport height increases significantly (> 100px)
      if (currentHeight - lastHeight > 100) {
        const activeElement = document.activeElement as HTMLInputElement;
        if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
          activeElement.blur();
        }
      }
      lastHeight = currentHeight;
    };

    window.visualViewport.addEventListener('resize', handleViewportResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, []);

  // Contact info from config for monument warning
  const [contactInfo, setContactInfo] = useState<{ phone: string; email: string }>({
    phone: '',
    email: ''
  });

  // Woonhuis discount config (hidden discount on non-glass coverages).
  // Initialize synchronously from the ConfigProvider value so callCalculation can bake the discount
  // into workflow data on the very first run (the async /config.json useEffect below would otherwise
  // race the calculation and leave undiscounted BTPs on downstream screens).
  const woonhuisDiscountRef = useRef<{ enabled: boolean; percentage: number; excludeCoverages: string[]; applyToData: boolean }>((() => {
    const cfg = (config as any)?.woonhuisDiscount;
    return cfg?.enabled
      ? {
          enabled: true,
          percentage: Number(cfg.percentage) || 0,
          excludeCoverages: Array.isArray(cfg.excludeCoverages) ? cfg.excludeCoverages : [],
          applyToData: cfg.applyToData !== false,
        }
      : { enabled: false, percentage: 0, excludeCoverages: [], applyToData: true };
  })());
  // Keep the ref in sync if the ConfigProvider's config resolves after mount.
  useEffect(() => {
    const cfg = (config as any)?.woonhuisDiscount;
    if (cfg?.enabled) {
      woonhuisDiscountRef.current = {
        enabled: true,
        percentage: Number(cfg.percentage) || 0,
        excludeCoverages: Array.isArray(cfg.excludeCoverages) ? cfg.excludeCoverages : [],
        applyToData: cfg.applyToData !== false,
      };
    }
  }, [config]);

  // Store undiscounted data for workflow persistence (when applyToData is false)
  const undiscountedDataRef = useRef<any>(null);

  // Wrapper: saves undiscounted premiums to workflow when applyToData is false
  // The ref is only populated when discount is active AND applyToData is false
  const saveWorkflowData = useCallback((stepId: string, displayData: any) => {
    if (undiscountedDataRef.current?.Contractdocument?.PP) {
      // Replace premium fields with undiscounted values from ref
      const saveData = JSON.parse(JSON.stringify(displayData));
      const srcPP = undiscountedDataRef.current.Contractdocument.PP;
      const dstPP = saveData?.Contractdocument?.PP;
      if (dstPP && srcPP) {
        // Copy section-level premiums from undiscounted source
        if (srcPP.OP && dstPP.OP) {
          const srcOp = Array.isArray(srcPP.OP) ? srcPP.OP[0] : srcPP.OP;
          const dstOp = Array.isArray(dstPP.OP) ? dstPP.OP[0] : dstPP.OP;
          if (srcOp && dstOp) {
            for (const k of ['OP_NTP', 'OP_TPP', 'OP_TASS', 'OP_BTP', 'OP_TTOT']) {
              if (srcOp[k] !== undefined) dstOp[k] = srcOp[k];
            }
          }
        }
        if (srcPP.GP && dstPP.GP) {
          for (const k of ['GP_NTP', 'GP_TPP', 'GP_TASS', 'GP_BTP', 'GP_TTOT']) {
            if (srcPP.GP[k] !== undefined) dstPP.GP[k] = srcPP.GP[k];
          }
        }
        // Copy PP-level totals
        for (const k of ['PP_NTP', 'PP_TPP', 'PP_TASS', 'PP_BTP', 'PP_TTOT', 'PP_NTPVPK']) {
          if (srcPP[k] !== undefined) dstPP[k] = srcPP[k];
        }
      }
      saveStepData(stepId, saveData);
    } else {
      saveStepData(stepId, displayData);
    }
  }, [saveStepData]);

  // Address is editable everywhere — changes propagate to every other selected product
  // via syncAddressToOtherProducts.
  const [isAddressLocked] = useState(false);
  const [isBuildingDetailsLocked] = useState(false);


  // Infofolio machinery (property-info fetch, monument flag, infofolio field locks,
  // pending-address trigger) lives in woonhuis/useInfofolio; loadSchema (setPendingInfofolioAddress)
  // and handleCustomFieldChange's BA_MONUMEN case (setIsMonumentProperty) below still drive the
  // returned setters. Must be called BEFORE useCoverageCalculation (consumes isMonumentProperty).
  const {
    isMonumentProperty,
    setIsMonumentProperty,
    isLoadingPropertyInfo,
    infofolioFilledFields,
    setPendingInfofolioAddress,
    refetchForAddress,
  } = useInfofolio({
    selectedProducts,
    getStepData,
    saveStepData,
    saveWorkflowData,
    setData,
    setValidationErrors,
    isBuildingDetailsLocked,
  });

  // Premium-calculation machinery (states/refs/auto-calc effect/callCalculation) lives in
  // woonhuis/useCoverageCalculation; loadSchema, handleNext and handleManualCalculate below
  // still drive the returned state/refs.
  const {
    isCalculating,
    setIsCalculating,
    setCalculationError,
    calculationSuccess,
    setCalculationSuccess,
    lastCalculationInput,
    lastCalculationOutput,
    setShouldAutoCalculate,
    initialLoadComplete,
    isUpdatingFromCalculation,
    lastCalculatedData,
    originalPremiumsRef,
    callCalculation,
  } = useCoverageCalculation({
    screenId: SCREEN_ID,
    data,
    setData,
    saveWorkflowData,
    t,
    setValidationErrors,
    loading,
    isMonumentProperty,
    isAddressLocked,
    isBuildingDetailsLocked,
    ppEkst,
    woonhuisDiscountRef,
    undiscountedDataRef,
  });

  // Last risk-address identity (pcode|huisnr|toevoeg) we propagated to the shared store, so the
  // propagation effect below only fires on a real change and never echoes a value that flowed in.
  const lastPropagatedAddress = useRef<string>('');

  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    // Check if woonhuis product is selected
    if (!selectedProducts?.woonhuis) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = relationData?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // Load contact info from config for monument warning
  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const response = await fetch(getConfigPathForBrand(getCurrentBrand()));
        const config = await response.json();
        const phone = config.texts?.contact?.phone || config.customer?.contact?.phone?.main || '';
        const email = config.texts?.contact?.email || config.customer?.contact?.email || '';
        setContactInfo({ phone, email });

        // Load woonhuis discount config
        if (config.woonhuisDiscount?.enabled) {
          woonhuisDiscountRef.current = {
            enabled: true,
            percentage: config.woonhuisDiscount.percentage || 0,
            excludeCoverages: config.woonhuisDiscount.excludeCoverages || [],
            applyToData: config.woonhuisDiscount.applyToData !== false
          };
        }
      } catch (error) {
        // Fallback values if config fails to load
        console.warn('Failed to load contact info from config');
      }
    };
    loadContactInfo();
  }, []);

  // Load schema from API for default values
  useEffect(() => {
    const loadSchema = async () => {
      try {

        const response = await fetch(`/screens/${SCREEN_ID}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load schema for ${SCREEN_ID}`);
        }
        const screenData = await response.json();
        const loadedSchema = screenData.jsonFormSchema;

        setSchema(loadedSchema);

        // Extract defaults from schema and merge with existing data
        const schemaDefaults = extractDefaults(loadedSchema);
        const existingData = getStepData(SCREEN_ID) || {};
        const mergedData = deepMerge(existingData, schemaDefaults);

        // Apply product-specific defaults
        // DISABLED: Testing which fields are actually required for calculation
        // const dataWithProductDefaults = _applyProductDefaults(mergedData);
        const dataWithProductDefaults = mergedData;

        if (dataWithProductDefaults?.Contractdocument?.PP) {
          // PP_STATUS - Status polis. The package endpoint (/productservice/package) only accepts
          // contracts with PP_STATUS in {7,8,9,10,14}; "8" is what the BFF calculate response returns
          // at offer stage. Seeding it here guarantees a valid status even when the slice is persisted
          // before a successful calculate (otherwise the package call rejects the whole document).
          if (!dataWithProductDefaults.Contractdocument.PP.PP_STATUS) {
            dataWithProductDefaults.Contractdocument.PP.PP_STATUS = '8';
          }
          if (dataWithProductDefaults.Contractdocument.PP.OP) {
            dataWithProductDefaults.Contractdocument.PP.OP.OP_PPRC = 20;
          }
          if (dataWithProductDefaults.Contractdocument.PP.GP) {
            dataWithProductDefaults.Contractdocument.PP.GP.GP_PPRC = 20;
          }
          // VR (Verhuur) not offered for SAA - remove if present from schema defaults
          if (dataWithProductDefaults.Contractdocument.PP.VR) {
            delete dataWithProductDefaults.Contractdocument.PP.VR;
          }
        }

        // Clear stored street and city values if not provided by relation data OR existing saved data
        // This prevents old test/cached values from appearing, but preserves user-entered/looked-up data
        // NOTE: Woonhuis is FIRST product in workflow - address is entered here
        const inboedelData = getStepData('inboedel');
        const inboedelBA = inboedelData?.Contractdocument?.PP?.BA;
        const existingBA = existingData?.Contractdocument?.PP?.BA;
        const existingVP = existingData?.Contractdocument?.PP?.VP;

        // Only delete street if it's not in relation data AND not in existing saved data (BA or VP)
        if (!relationData?.clientInfo?.street && !inboedelBA?.BA_STRAAT && !existingBA?.BA_STRAAT) {
          if (dataWithProductDefaults?.Contractdocument?.PP?.BA) {
            delete dataWithProductDefaults.Contractdocument.PP.BA.BA_STRAAT;
          }
          if (!existingVP?.VP_STRAAT) {
            if (dataWithProductDefaults?.Contractdocument?.PP?.VP) {
              delete dataWithProductDefaults.Contractdocument.PP.VP.VP_STRAAT;
            }
            if (dataWithProductDefaults?.Relatiedocument?.VP) {
              delete dataWithProductDefaults.Relatiedocument.VP.VP_STRAAT;
            }
          }
        }

        // Only delete city if it's not in relation data, Inboedel, AND not in existing saved data (BA or VP)
        if (!relationData?.clientInfo?.city && !inboedelBA?.BA_PLAATS && !existingBA?.BA_PLAATS) {
          if (dataWithProductDefaults?.Contractdocument?.PP?.BA) {
            delete dataWithProductDefaults.Contractdocument.PP.BA.BA_PLAATS;
          }
          if (!existingVP?.VP_PLAATS) {
            if (dataWithProductDefaults?.Contractdocument?.PP?.VP) {
              delete dataWithProductDefaults.Contractdocument.PP.VP.VP_PLAATS;
            }
            if (dataWithProductDefaults?.Relatiedocument?.VP) {
              delete dataWithProductDefaults.Relatiedocument.VP.VP_PLAATS;
            }
          }
        }

        // Link relation data from client-information page
        const dataWithRelationData = linkRelationData(dataWithProductDefaults, relationData, { now: new Date() });

        // Link Inboedel-specific data (building details). Address handling itself follows
        // sharedAddress as the canonical source below.
        let dataWithInboedelAddress = linkInboedelAddress(dataWithRelationData, inboedelData, getStepData);

        // sharedAddress is the canonical policyholder address — always overwrite local
        // VP (and BA when the risk address equals the policyholder address) with it when
        // populated, so an edit made elsewhere flows in here on every visit. Falls back
        // to scanning other products' step data only when sharedAddress hasn't been seeded.
        const sourceBA = (sharedAddress?.BA_PCODE && sharedAddress?.BA_HUISNR)
          ? sharedAddress
          : findUpstreamAddress(getStepData, 'woonhuis');
        if (sourceBA?.BA_PCODE && sourceBA?.BA_HUISNR) {
          const huisnrNum = Number(sourceBA.BA_HUISNR);
          const vpHuisnr = isNaN(huisnrNum) ? 0 : huisnrNum;
          const syncBA = (dataWithInboedelAddress?.Supportsection?.Divers?.RisicoAdresGelijk ?? 'J') === 'J';
          dataWithInboedelAddress = {
            ...dataWithInboedelAddress,
            Contractdocument: {
              ...dataWithInboedelAddress.Contractdocument,
              PP: {
                ...dataWithInboedelAddress.Contractdocument?.PP,
                ...(syncBA ? {
                  BA: {
                    ...dataWithInboedelAddress.Contractdocument?.PP?.BA,
                    BA_PCODE: sourceBA.BA_PCODE,
                    BA_HUISNR: sourceBA.BA_HUISNR,
                    BA_TOEVOEG: sourceBA.BA_TOEVOEG || '',
                    BA_STRAAT: sourceBA.BA_STRAAT || '',
                    BA_PLAATS: sourceBA.BA_PLAATS || '',
                  },
                } : {}),
                VP: {
                  ...dataWithInboedelAddress.Contractdocument?.PP?.VP,
                  VP_PCODE: sourceBA.BA_PCODE,
                  VP_HUISNR: vpHuisnr,
                  VP_TOEVOEG: sourceBA.BA_TOEVOEG || '',
                  VP_STRAAT: sourceBA.BA_STRAAT || '',
                  VP_PLAATS: sourceBA.BA_PLAATS || '',
                },
              },
            },
          };
        }

        // Preserve existing VP address fields from saved data (policyholder address / Adresgegevens)
        // These are set by the VP address lookup and must not be lost during initialization
        const savedVP = existingData?.Contractdocument?.PP?.VP;
        if (savedVP && dataWithInboedelAddress?.Contractdocument?.PP?.VP) {
          if (savedVP.VP_PCODE && !dataWithInboedelAddress.Contractdocument.PP.VP.VP_PCODE) {
            dataWithInboedelAddress.Contractdocument.PP.VP.VP_PCODE = savedVP.VP_PCODE;
          }
          if (savedVP.VP_HUISNR && !dataWithInboedelAddress.Contractdocument.PP.VP.VP_HUISNR) {
            dataWithInboedelAddress.Contractdocument.PP.VP.VP_HUISNR = savedVP.VP_HUISNR;
          }
          if (savedVP.VP_TOEVOEG && !dataWithInboedelAddress.Contractdocument.PP.VP.VP_TOEVOEG) {
            dataWithInboedelAddress.Contractdocument.PP.VP.VP_TOEVOEG = savedVP.VP_TOEVOEG;
          }
          if (savedVP.VP_STRAAT && !dataWithInboedelAddress.Contractdocument.PP.VP.VP_STRAAT) {
            dataWithInboedelAddress.Contractdocument.PP.VP.VP_STRAAT = savedVP.VP_STRAAT;
          }
          if (savedVP.VP_PLAATS && !dataWithInboedelAddress.Contractdocument.PP.VP.VP_PLAATS) {
            dataWithInboedelAddress.Contractdocument.PP.VP.VP_PLAATS = savedVP.VP_PLAATS;
          }
        }

        // Woonhuis is FIRST product in workflow - no address or building locking needed

        // If valid address exists and no Infofolio data exists, trigger lookup
        const finalBA = dataWithInboedelAddress?.Contractdocument?.PP?.BA;
        const hasInfofolioData = finalBA?.BA_BOUWJR || finalBA?.BA_TYPEWON || finalBA?.BA_HERBWRD;
        const hasValidAddress = finalBA?.BA_PCODE && finalBA?.BA_HUISNR;
        if (hasValidAddress && !hasInfofolioData) {
          // Use setTimeout to set state after the current render cycle completes
          setTimeout(() => {
            setPendingInfofolioAddress({
              postalCode: finalBA.BA_PCODE,
              houseNumber: finalBA.BA_HUISNR,
              addition: finalBA.BA_TOEVOEG || undefined
            });
          }, 100);
        }

        // Set required default: PP_BETTERM (payment term in months)
        if (!dataWithInboedelAddress.Contractdocument?.PP?.PP_BETTERM) {
          dataWithInboedelAddress.Contractdocument.PP.PP_BETTERM = 1;
        }

        // Set required default: PP_INGDAT (insurance start date in YYYYMMDD format)
        if (!dataWithInboedelAddress.Contractdocument?.PP?.PP_INGDAT) {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const dateInN8Format = `${year}${month}${day}`;
          dataWithInboedelAddress.Contractdocument.PP.PP_INGDAT = Number(dateInN8Format);
        }

        // Set required default: OP_STORM 'N' (not waived) + matching OP_ERBSTRM 250
        if (!dataWithInboedelAddress.Contractdocument?.PP?.OP?.OP_STORM) {
          dataWithInboedelAddress.Contractdocument.PP.OP.OP_STORM = 'N';
          if (dataWithInboedelAddress.Contractdocument.PP.OP.OP_ERBSTRM === undefined ||
              dataWithInboedelAddress.Contractdocument.PP.OP.OP_ERBSTRM === null) {
            dataWithInboedelAddress.Contractdocument.PP.OP.OP_ERBSTRM = 250;
          }
        }

        // OP_GSS (family composition) - user must fill, no default

        // Rebuild value determination - no default, user must select

        // Monument property - default to 'N' if not set
        if (!dataWithInboedelAddress.Contractdocument.PP.BA.BA_MONUMEN) {
          dataWithInboedelAddress.Contractdocument.PP.BA.BA_MONUMEN = 'N';
        }

        // Prefill Gezinssamenstelling from any value the user already picked on any product
        // (including one they later deselected) — it is entered once and shared across products.
        if (!dataWithInboedelAddress.Contractdocument?.PP?.OP?.OP_GSS) {
          const sharedGss = findSharedFamilyComposition(getStepData);
          if (sharedGss) {
            if (!dataWithInboedelAddress.Contractdocument.PP.OP) dataWithInboedelAddress.Contractdocument.PP.OP = {};
            dataWithInboedelAddress.Contractdocument.PP.OP.OP_GSS = sharedGss;
          }
        }

        setData(dataWithInboedelAddress);

        // IMPORTANT: Save the linked data to workflow so it's available when building the request
        // Clean unselected coverage premiums before saving
        const cleanedInitialData = cleanUnselectedCoveragePremiums(dataWithInboedelAddress);
        saveWorkflowData(SCREEN_ID, cleanedInitialData);

        setLoading(false);

        // Check if calculation data already exists (user navigated back to this page)
        // If premium data exists, it means a calculation was already done
        const hasPremiumData = dataWithInboedelAddress?.Contractdocument?.PP?.PP_NTP;
        if (hasPremiumData) {
          setCalculationSuccess(true);

          // Restore original premiums from PP sections (for base price display)
          // BFF returns some values as strings - ensure all are numbers
          const pp = dataWithInboedelAddress?.Contractdocument?.PP;
          originalPremiumsRef.current = {
            PP_TTOT: Number(pp?.PP_TTOT) || 0,
            OP_TTOT: Number(pp?.OP?.OP_TTOT) || 0,
            GP_TTOT: Number(pp?.GP?.GP_TTOT) || 0,
          };

          // Create input signature and store it to prevent auto-recalculation
          const ba = dataWithInboedelAddress?.Contractdocument?.PP?.BA;
          const op = dataWithInboedelAddress?.Contractdocument?.PP?.OP;
          const vp = dataWithInboedelAddress?.Contractdocument?.PP?.VP;

          // NOTE: This signature MUST match the auto-calculation signature below
          // to prevent unnecessary recalculation when navigating back to this page
          const inputSignature = JSON.stringify({
            BA_PCODE: ba?.BA_PCODE,
            BA_HUISNR: ba?.BA_HUISNR,
            BA_STRAAT: ba?.BA_STRAAT,
            BA_PLAATS: ba?.BA_PLAATS,
            BA_BOUWJR: ba?.BA_BOUWJR,
            BA_TYPEWON: ba?.BA_TYPEWON,
            BA_AARDBW: ba?.BA_AARDBW,
            BA_AARDDAK: ba?.BA_AARDDAK,
            BA_WONING: ba?.BA_WONING,
            BA_HERBVST: ba?.BA_HERBVST,
            BA_TOEVOEG: ba?.BA_TOEVOEG,
            BA_BDRFBEL: ba?.BA_BDRFBEL,
            BA_OPPWON: ba?.BA_OPPWON,
            BA_EIGENWO: ba?.BA_EIGENWO,
            BA_PERMBEW: ba?.BA_PERMBEW,
            RisicoAdresGelijk: dataWithInboedelAddress?.Supportsection?.Divers?.RisicoAdresGelijk,
            OP_GSS: op?.OP_GSS,
            OP_VERZSOM: op?.OP_VERZSOM,
            OP_ERB: op?.OP_ERB,
            OP_STORM: op?.OP_STORM,
            VP_GEBDAT: vp?.VP_GEBDAT,
            Hoofddekking: dataWithInboedelAddress?.Supportsection?.Dekkingen?.Hoofddekking
            // NOTE: Glas coverage selection excluded from signature
            // to prevent recalculation when toggling - prices are pre-calculated
          });

          lastCalculatedData.current = inputSignature;
        }

        // Mark initial load as complete to enable auto-calculation
        setTimeout(() => {
          initialLoadComplete.current = true;
          // Trigger auto-calculation check via state update
          setShouldAutoCalculate(true);
        }, 1000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load schema');
        setLoading(false);
      }
    };

    loadSchema();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only schema load; its inputs are read once
  }, []);



  // Sync from shared address (single source of truth) — every change flows into data.VP (and
  // data.BA when the risk address equals the policyholder address) so the calc payload and the
  // AddressSection value stay aligned with whichever product last edited the policyholder address.
  useEffect(() => {
    // Only update local data state after loading completes
    if (!loading && (sharedAddress.BA_PCODE || sharedAddress.BA_HUISNR)) {
      setData((prevData: any) => {
        const currentBA = prevData?.Contractdocument?.PP?.BA || {};
        const currentVP = prevData?.Contractdocument?.PP?.VP || {};
        // Need-update covers BA AND VP — schema-load may have already aligned BA from
        // sharedAddress without touching VP, in which case BA matches but VP still
        // lacks VP_STRAAT/VP_PLAATS (and the validated indicator stays off).
        const needsUpdate =
          (sharedAddress.BA_PCODE && currentBA.BA_PCODE !== sharedAddress.BA_PCODE) ||
          (sharedAddress.BA_HUISNR && currentBA.BA_HUISNR !== sharedAddress.BA_HUISNR) ||
          (sharedAddress.BA_TOEVOEG !== undefined && currentBA.BA_TOEVOEG !== sharedAddress.BA_TOEVOEG) ||
          (sharedAddress.BA_STRAAT && currentBA.BA_STRAAT !== sharedAddress.BA_STRAAT) ||
          (sharedAddress.BA_PLAATS && currentBA.BA_PLAATS !== sharedAddress.BA_PLAATS) ||
          (sharedAddress.BA_PCODE && currentVP.VP_PCODE !== sharedAddress.BA_PCODE) ||
          (sharedAddress.BA_STRAAT && currentVP.VP_STRAAT !== sharedAddress.BA_STRAAT) ||
          (sharedAddress.BA_PLAATS && currentVP.VP_PLAATS !== sharedAddress.BA_PLAATS);

        if (!needsUpdate) return prevData;

        // VP (policyholder) always syncs from sharedAddress. BA syncs only when the
        // risk address is the same as the policyholder.
        return {
          ...prevData,
          Contractdocument: {
            ...prevData.Contractdocument,
            PP: {
              ...prevData.Contractdocument?.PP,
              ...(risicoAdresGelijk === 'J' ? {
                BA: {
                  ...prevData.Contractdocument?.PP?.BA,
                  ...(sharedAddress.BA_PCODE && { BA_PCODE: sharedAddress.BA_PCODE }),
                  ...(sharedAddress.BA_HUISNR && { BA_HUISNR: sharedAddress.BA_HUISNR }),
                  ...(sharedAddress.BA_TOEVOEG !== undefined && { BA_TOEVOEG: sharedAddress.BA_TOEVOEG }),
                  ...(sharedAddress.BA_STRAAT && { BA_STRAAT: sharedAddress.BA_STRAAT }),
                  ...(sharedAddress.BA_PLAATS && { BA_PLAATS: sharedAddress.BA_PLAATS }),
                },
              } : {}),
              VP: {
                ...prevData.Contractdocument?.PP?.VP,
                ...(sharedAddress.BA_PCODE && { VP_PCODE: sharedAddress.BA_PCODE }),
                ...(sharedAddress.BA_HUISNR && { VP_HUISNR: Number(sharedAddress.BA_HUISNR) || 0 }),
                ...(sharedAddress.BA_TOEVOEG !== undefined && { VP_TOEVOEG: sharedAddress.BA_TOEVOEG }),
                ...(sharedAddress.BA_STRAAT && { VP_STRAAT: sharedAddress.BA_STRAAT }),
                ...(sharedAddress.BA_PLAATS && { VP_PLAATS: sharedAddress.BA_PLAATS }),
              },
            },
          },
        };
      });
    }
  }, [loading, sharedAddress, risicoAdresGelijk]);

  // Single, reliable propagation path: whenever woonhuis's committed risk address (BA) changes,
  // push it to the shared store and the other products — regardless of which trigger (postcode
  // blur, house-number blur, debounced auto-lookup, autocomplete) caused the change. The per-input
  // handlers historically only propagated on a successful, non-deduped lookup, so an in-place edit
  // could silently fail to reach Inboedel. This decouples propagation from those fragile triggers.
  useEffect(() => {
    if (loading) return;
    // Only the policyholder address belongs in the shared store. When the risk address differs
    // (RisicoAdresGelijk 'N') BA is the insured-property address and must NOT flow into
    // sharedAddress — that's owned by the dedicated risk slice instead.
    if (risicoAdresGelijk !== 'J') return;

    // data.BA only changes on a *commit* (blur / debounced lookup / handleCustomFieldChange),
    // never on raw keystrokes (those touch input state only), so it's safe to react to it here.
    const ba = data?.Contractdocument?.PP?.BA;
    if (!ba?.BA_PCODE || !ba?.BA_HUISNR) return;

    const key = `${ba.BA_PCODE}|${ba.BA_HUISNR}|${ba.BA_TOEVOEG || ''}`;
    if (key === lastPropagatedAddress.current) return;

    // If the shared store already matches this identity, the value flowed in FROM sharedAddress
    // (via the mirror effect above) — record it and skip to avoid an echo loop.
    const sharedMatches =
      sharedAddress.BA_PCODE === ba.BA_PCODE &&
      String(sharedAddress.BA_HUISNR ?? '') === String(ba.BA_HUISNR) &&
      (sharedAddress.BA_TOEVOEG || '') === (ba.BA_TOEVOEG || '');
    if (sharedMatches) {
      lastPropagatedAddress.current = key;
      return;
    }

    lastPropagatedAddress.current = key;
    const fullAddress = {
      BA_PCODE: ba.BA_PCODE,
      BA_HUISNR: ba.BA_HUISNR,
      BA_TOEVOEG: ba.BA_TOEVOEG || '',
      BA_STRAAT: ba.BA_STRAAT || '',
      BA_PLAATS: ba.BA_PLAATS || '',
    };
    updateSharedAddress(fullAddress);
    syncAddressToOtherProducts(SCREEN_ID, fullAddress, selectedProducts, getStepData, saveStepData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    risicoAdresGelijk,
    data?.Contractdocument?.PP?.BA?.BA_PCODE,
    data?.Contractdocument?.PP?.BA?.BA_HUISNR,
    data?.Contractdocument?.PP?.BA?.BA_TOEVOEG,
    data?.Contractdocument?.PP?.BA?.BA_STRAAT,
    data?.Contractdocument?.PP?.BA?.BA_PLAATS,
  ]);

  // Address autocomplete lookup with debouncing and cancellation
  // Risk-address Ja/Nee answer. Shared across property products (they insure the same
  // property). 'J' => insured risk address equals the policyholder address (BA mirrors VP);
  // 'N' => a separate "Adres verzekerde woning" is collected. Switching to 'N' starts EMPTY
  // unless a sibling property product already supplied a risk address (then reuse it).
  const handleRiskAddressSameChange = useCallback((newValue: 'J' | 'N') => {
    setRisicoAdresGelijk(newValue);
    setRiskAddressSame(newValue);
    setData((prev: any) => {
      const vp = prev?.Contractdocument?.PP?.VP || {};
      let nextBA: any = { ...(prev?.Contractdocument?.PP?.BA || {}) };
      if (newValue === 'J') {
        nextBA = { ...nextBA, ...vpAddressAsBa(vp) };
      } else {
        const upstream = findUpstreamRiskAddress(getStepData, SCREEN_ID);
        if (upstream) {
          nextBA = {
            ...nextBA,
            BA_PCODE: upstream.BA_PCODE,
            BA_HUISNR: upstream.BA_HUISNR,
            BA_TOEVOEG: upstream.BA_TOEVOEG || '',
            BA_STRAAT: upstream.BA_STRAAT || '',
            BA_PLAATS: upstream.BA_PLAATS || '',
          };
        } else {
          delete nextBA.BA_PCODE;
          delete nextBA.BA_HUISNR;
          delete nextBA.BA_TOEVOEG;
          delete nextBA.BA_STRAAT;
          delete nextBA.BA_PLAATS;
        }
      }
      const updated = {
        ...prev,
        Supportsection: {
          ...prev?.Supportsection,
          Divers: { ...prev?.Supportsection?.Divers, RisicoAdresGelijk: newValue },
        },
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: { ...prev?.Contractdocument?.PP, BA: nextBA },
        },
      };
      stageAddressSave(() => saveWorkflowData(SCREEN_ID, cleanUnselectedCoveragePremiums(updated)));
      return updated;
    });
    scheduleAddressSave();
    lastCalculatedData.current = '';
  }, [setRiskAddressSame, getStepData, saveWorkflowData, stageAddressSave, scheduleAddressSave, lastCalculatedData]);

  // The risk-address Ja/Nee question, rendered inside the Adresgegevens card (above the postcode)
  // via AddressSection's topContent slot.
  const riskAddressQuestion = (
    <div style={styles.mb16}>
      <label style={styles.fieldLabel} htmlFor="risicoadres">
        {t('screens.woonhuis.fields.riskAddressSame')}
      </label>
      <div style={styles.radioGroup}>
        {(['J', 'N'] as const).map((optValue) => {
          const selected = risicoAdresGelijk === optValue;
          return (
            <div
              key={optValue}
              style={selected ? styles.radioOptionSelected : styles.radioOption}
              onClick={() => handleRiskAddressSameChange(optValue)}
            >
              <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                {selected && <div style={styles.radioDotInner} />}
              </div>
              {optValue === 'J' ? t('common.yes') : t('common.no')}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Policyholder (VP) address, emitted by the shared AddressSection. AddressSection owns the
  // postcode lookup, multiple-match chooser and validation; here we only persist the result into
  // VP (and into BA when the risk address equals it). Cross-product propagation is handled by the
  // BA propagation effect (J) and by AddressSection's own shared-address write.
  const handlePolicyholderAddressChange = useCallback((a: Address) => {
    const huisnrNum = Number(a.houseNumber);
    const vpHuisnr = isNaN(huisnrNum) ? 0 : huisnrNum;
    const same = risicoAdresGelijk === 'J';

    setData((prev: any) => {
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            VP: {
              ...prev?.Contractdocument?.PP?.VP,
              VP_PCODE: a.postcode,
              VP_HUISNR: vpHuisnr,
              VP_TOEVOEG: a.addition || '',
              VP_STRAAT: a.street || '',
              VP_PLAATS: a.city || '',
            },
            ...(same ? {
              BA: {
                ...prev?.Contractdocument?.PP?.BA,
                BA_PCODE: a.postcode,
                BA_HUISNR: a.houseNumber,
                BA_TOEVOEG: a.addition || '',
                BA_STRAAT: a.street || '',
                BA_PLAATS: a.city || '',
              },
            } : {}),
          },
        },
      };
      stageAddressSave(() => saveWorkflowData(SCREEN_ID, cleanUnselectedCoveragePremiums(updated)));
      return updated;
    });
    scheduleAddressSave();

    setValidationErrors(prev => {
      const u = { ...prev };
      delete u['Contractdocument.PP.BA.BA_PCODE'];
      delete u['Contractdocument.PP.BA.BA_HUISNR'];
      delete u['Contractdocument.PP.BA.BA_STRAAT'];
      return u;
    });

    // Refetch building data only when this address is the insured risk (J) and it resolved.
    if (same && a.street && a.city) {
      refetchForAddress(a);
    }
  }, [risicoAdresGelijk, saveWorkflowData, refetchForAddress, stageAddressSave, scheduleAddressSave]);

  // Insured-property (risk) address, used only when RisicoAdresGelijk is 'N'. Writes BA only
  // (never VP), refetches Infofolio for the risk postcode, and shares the risk address across
  // property products via the dedicated risk slice. shareAddress is false on this AddressSection
  // so it never overwrites the policyholder sharedData.address.
  const handleRiskAddressChange = useCallback((a: Address) => {
    setData((prev: any) => {
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            BA: {
              ...prev?.Contractdocument?.PP?.BA,
              BA_PCODE: a.postcode,
              BA_HUISNR: a.houseNumber,
              BA_TOEVOEG: a.addition || '',
              BA_STRAAT: a.street || '',
              BA_PLAATS: a.city || '',
            },
          },
        },
      };
      stageAddressSave(() => saveWorkflowData(SCREEN_ID, cleanUnselectedCoveragePremiums(updated)));
      return updated;
    });
    scheduleAddressSave();

    setValidationErrors(prev => {
      const u = { ...prev };
      delete u['Contractdocument.PP.BA.BA_PCODE'];
      delete u['Contractdocument.PP.BA.BA_HUISNR'];
      delete u['Contractdocument.PP.BA.BA_STRAAT'];
      return u;
    });

    const riskBA = {
      BA_PCODE: a.postcode,
      BA_HUISNR: a.houseNumber,
      BA_TOEVOEG: a.addition || '',
      BA_STRAAT: a.street || '',
      BA_PLAATS: a.city || '',
    };
    updateRiskAddress(riskBA, SCREEN_ID);
    syncRiskAddressToProperties(SCREEN_ID, riskBA, selectedProducts, getStepData, saveStepData);

    if (a.street && a.city) {
      refetchForAddress(a);
    }
  }, [selectedProducts, getStepData, saveStepData, saveWorkflowData, updateRiskAddress, refetchForAddress, stageAddressSave, scheduleAddressSave]);


  // Handle custom field changes
  // Numeric range validation for the building number fields (forwarded from WoningSpecificaties onBlur).
  // The value itself is already committed via handleCustomFieldChange onChange; this only sets/clears errors.
  const handleBuildingNumberBlur = useCallback((fieldPath: string, raw: string) => {
    const entry = validateBuildingNumberField(fieldPath, raw, { now: new Date() });
    if (entry === undefined) return;
    if (entry) {
      setValidationErrors((prev) => ({ ...prev, [fieldPath]: t(entry.key, entry.params as any) as string }));
    } else {
      setValidationErrors((prev) => { const u = { ...prev }; delete u[fieldPath]; return u; });
    }
  }, [t]);

  // Updates central data state that will be used for BFF calculations
  const handleCustomFieldChange = useCallback((fieldPath: string, value: any) => {

    // Clear validation error for this field when user updates it
    if (validationErrors[fieldPath]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[fieldPath];
        return updated;
      });
    }

    // Leaving 'Vaste taxatie' hides (and clears) its sub-fields — drop their errors too
    if (fieldPath === 'Contractdocument.PP.BA.BA_HERBVST' && value !== 'V') {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated['Contractdocument.PP.BA.BA_EINDGTX'];
        delete updated['Supportsection.Divers.TaxatieVRT'];
        return updated;
      });
    }

    setData((prevData: any) => applyWoonhuisFieldUpdate(prevData, fieldPath, value, {
      risicoAdresGelijk,
      selectedProducts,
      getStepData,
      saveStepData,
      saveWorkflowData,
      relationData,
      setRelationData,
      setIsMonumentProperty,
      screenId: SCREEN_ID,
    }));

  }, [validationErrors, relationData, setRelationData, selectedProducts, getStepData, saveStepData, saveWorkflowData, setIsMonumentProperty, risicoAdresGelijk]);


  // Validate form fields — pure rules live in woonhuis/validation.ts; this wrapper translates
  // the {key, params} entries and pushes them into state.
  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const entries = validateWoonhuis(data, {
      now: new Date(),
      isAddressLocked,
      isBuildingDetailsLocked,
    });
    const errors: Record<string, string> = Object.fromEntries(
      Object.entries(entries).map(([field, e]) => [field, t(e.key, e.params as any) as string])
    );

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return { isValid: false, errors };
    }

    return { isValid: true, errors: {} };
  }, [data, t, isAddressLocked, isBuildingDetailsLocked]);

  // Re-translate existing validation errors when language changes
  useEffect(() => {
    // Only re-validate if there are existing errors (to update their translations)
    setValidationErrors(prevErrors => {
      if (Object.keys(prevErrors).length === 0) {
        return prevErrors;
      }
      // Re-validate to get translated messages
      const { errors } = validateForm();
      return errors;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Manual calculation trigger
  const handleManualCalculate = () => {
    const { isValid, errors } = validateForm();
    if (!isValid) {
      setCalculationError(t('screens.woonhuis.validation.fillRequiredFields'));
      // Scroll to first field with validation error
      scrollToFirstError(errors);
      return;
    }
    callCalculation(data, true);
  };

  const handleNext = () => {
    // Block navigation if property is a monument
    if (isMonumentProperty) {
      // Scroll to the monument warning
      const monumentWarning = document.getElementById('monument-warning');
      if (monumentWarning) {
        monumentWarning.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const { isValid, errors } = validateForm();
    if (!isValid) {
      scrollToFirstError(errors);
      return;
    }

    // Check if calculation has been done before allowing navigation
    if (!calculationSuccess) {
      const calcError = { '_calculation': t('screens.woonhuis.validation.calculateFirst') };
      setValidationErrors(calcError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Capture current data and navigation target before async work
    const currentData = JSON.parse(JSON.stringify(data));
    const nextScreen = getNextScreen('/workflow/woonhuis', selectedProducts);
    if (!nextScreen) return;

    // Run final calculation with only SELECTED coverages to get correct CL/VW records.
    // Uses .then() pattern (NOT async/await) to keep handleNext synchronous.
    // IMPORTANT: Does NOT call setData() to avoid triggering auto-calc useEffect.
    const requestBody = mapToCalculationRequest(currentData, ppEkst, { baseUrl: window.location.origin + '/', now: new Date() });

    const finalFingerprint = stableStringify(requestBody);
    if (finalFingerprint === sessionStorage.getItem('woonhuis_final_payload')) {
      navigate(nextScreen);
      return;
    }

    setIsCalculating(true);

    // GP (Glass) is rated by the Midglas engine over BA_HERBWRD (see utils/glassPremium.ts).
    bffClient.request<any>('POST', '/api/v1/productservice/calculate', requestBody)
      .then((result: any) => {
        let dataToSave = currentData;
        if (result) {
          isUpdatingFromCalculation.current = true;
          const mappedData = mapFromCalculationResponse(result, currentData);
          dataToSave = { ...currentData, ...mappedData };
          // Re-bake the hidden discount: the BFF response carries undiscounted sections, so
          // merging it back would otherwise overwrite the baked values from the live calc.
          applyWoonhuisDiscountInPlace(dataToSave, woonhuisDiscountRef.current);
        }
        const cleanedData = cleanUnselectedCoveragePremiums(dataToSave);
        saveWorkflowData(SCREEN_ID, cleanedData);
        sessionStorage.setItem('woonhuis_final_payload',
          stableStringify(mapToCalculationRequest(cleanedData, ppEkst, { baseUrl: window.location.origin + '/', now: new Date() })));
      })
      .catch(() => {
        // Fallback: save current data without recalculated CL/VW
        const cleanedData = cleanUnselectedCoveragePremiums(currentData);
        saveWorkflowData(SCREEN_ID, cleanedData);
      })
      .finally(() => {
        setIsCalculating(false);
        navigate(nextScreen);
      });
  };

  const handlePrevious = () => {
    // Clean unselected coverage premiums before saving
    const cleanedData = cleanUnselectedCoveragePremiums(data);
    saveWorkflowData(SCREEN_ID, cleanedData);
    const previousScreen = getPreviousScreen('/workflow/woonhuis', selectedProducts);
    if (previousScreen) {
      navigate(previousScreen);
    }
  };

  if (loading) {
    return <LoadingScreen message={t('common.loadingSchema')} />;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>{t('common.errorLoadingSchema')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} style={styles.button}>
          {t('common.backToHome')}
        </button>
      </div>
    );
  }

  // Calculate dynamic step progress
  const currentStep = getCurrentStepNumber('/workflow/woonhuis', selectedProducts);
  const totalSteps = calculateTotalSteps(selectedProducts);

  // Calculate product counter (which product in the workflow)
  const selectedProductsList = getProductOrder().filter(product => selectedProducts?.[product]);
  const currentProductIndex = selectedProductsList.indexOf('woonhuis') + 1;
  const totalProducts = selectedProductsList.length;

  // Infofolio supplied these; the inputs only open when the lookup could not fill them.
  const rebuildValueFromInfofolio = infofolioFilledFields.has('verzsom') && !!data?.Contractdocument?.PP?.OP?.OP_VERZSOM;
  const rebuildValuationFromInfofolio = infofolioFilledFields.has('herbvst') && !!data?.Contractdocument?.PP?.BA?.BA_HERBVST;

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={styles.page}>
        <div style={styles.container}>
          <WithHelpSidebar stackEarly>
            <header style={styles.contentHeader}>
              <h1 style={styles.title}>{t('screens.woonhuis.title')}</h1>
            </header>

            {/* Form layout */}

            <div style={styles.formColumn}>
              {/* Adresgegevens — policyholder address hidden when an upstream selected product already supplied it */}
              {/* Adresgegevens (policyholder). Read-only summary when an upstream selected
                  product already supplied it; editable otherwise. */}
              {shouldHideAddressSection('woonhuis', selectedProducts, getStepData) ? (
                <AddressSection
                  readOnly
                  icon={Users}
                  title="screens.woonhuis.fields.policyholderAddress"
                  topContent={riskAddressQuestion}
                  value={{
                    postcode: sharedAddress?.BA_PCODE || data?.Contractdocument?.PP?.VP?.VP_PCODE || '',
                    houseNumber: sharedAddress?.BA_HUISNR != null ? String(sharedAddress.BA_HUISNR) : (data?.Contractdocument?.PP?.VP?.VP_HUISNR != null ? String(data.Contractdocument.PP.VP.VP_HUISNR) : ''),
                    addition: sharedAddress?.BA_TOEVOEG || data?.Contractdocument?.PP?.VP?.VP_TOEVOEG || '',
                    street: sharedAddress?.BA_STRAAT || data?.Contractdocument?.PP?.VP?.VP_STRAAT || '',
                    city: sharedAddress?.BA_PLAATS || data?.Contractdocument?.PP?.VP?.VP_PLAATS || '',
                  }}
                />
              ) : (
                <AddressSection
                  icon={Users}
                  title="screens.woonhuis.fields.policyholderAddress"
                  topContent={riskAddressQuestion}
                  value={{
                    postcode: data?.Contractdocument?.PP?.VP?.VP_PCODE || '',
                    houseNumber: data?.Contractdocument?.PP?.VP?.VP_HUISNR != null ? String(data.Contractdocument.PP.VP.VP_HUISNR) : '',
                    addition: data?.Contractdocument?.PP?.VP?.VP_TOEVOEG || '',
                    street: data?.Contractdocument?.PP?.VP?.VP_STRAAT || '',
                    city: data?.Contractdocument?.PP?.VP?.VP_PLAATS || '',
                  }}
                  onChange={handlePolicyholderAddressChange}
                />
              )}

              {/* Adres verzekerde woning (separate insured-property address when it differs).
                  Read-only summary when a sibling property product collected it; an editable
                  block when this screen did (or nobody yet). */}
              {risicoAdresGelijk === 'N' && (() => {
                const upstreamRisk = findSiblingRiskAddress(getStepData, SCREEN_ID, riskAddressSource);
                return upstreamRisk ? (
                  <AddressSection
                    key="risk-summary"
                    readOnly
                    icon={House}
                    shareAddress={false}
                    title="screens.woonhuis.fields.insuredPropertyAddress"
                    value={{
                      postcode: upstreamRisk.BA_PCODE || '',
                      houseNumber: upstreamRisk.BA_HUISNR != null ? String(upstreamRisk.BA_HUISNR) : '',
                      addition: upstreamRisk.BA_TOEVOEG || '',
                      street: upstreamRisk.BA_STRAAT || '',
                      city: upstreamRisk.BA_PLAATS || '',
                    }}
                  />
                ) : (
                  <AddressSection
                    key="risk-editable"
                    idPrefix="risk-address"
                    icon={House}
                    shareAddress={false}
                    title="screens.woonhuis.fields.insuredPropertyAddress"
                    value={{
                      postcode: data?.Contractdocument?.PP?.BA?.BA_PCODE || '',
                      houseNumber: data?.Contractdocument?.PP?.BA?.BA_HUISNR != null ? String(data.Contractdocument.PP.BA.BA_HUISNR) : '',
                      addition: data?.Contractdocument?.PP?.BA?.BA_TOEVOEG || '',
                      street: data?.Contractdocument?.PP?.BA?.BA_STRAAT || '',
                      city: data?.Contractdocument?.PP?.BA?.BA_PLAATS || '',
                    }}
                    onChange={handleRiskAddressChange}
                  />
                );
              })()}

          {/* Building Specifications Section */}
          <div style={styles.buildingSpecsContainer}>
            <div className="woonhuis-building-header" style={{ ...styles.sectionHeader, ...styles.rowWrapBetween }}>
              <div style={styles.rowCenter}>
                <div style={{ ...styles.sectionIconContainer, ...styles.mr12 }}>
                  <ClipboardText size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.iconOpaque} />
                </div>
                <h2 style={styles.addressTitle}>{t('screens.woonhuis.sections.buildingSpecs')}</h2>
              </div>
              {isLoadingPropertyInfo && (
                <div className="woonhuis-property-loading" style={styles.rowCenter8}>
                  <div style={styles.propertySpinner} />
                  <span style={styles.linkPrimary13}>
                    {t('common.fetchingPropertyDetails')}
                  </span>
                </div>
              )}
            </div>

            {/* Monument Warning - Show if property is detected as a monument */}
            {isMonumentProperty && (
              <PropertyWarning
                id="monument-warning"
                titleKey="screens.woonhuis.monumentWarning.title"
                messageKey="screens.woonhuis.monumentWarning.message"
                contactInfo={contactInfo}
              />
            )}

            {/* Rented Warning - shown when inboedel is selected AND BA_WONING is 'D'/'H'.
                Woonhuis itself can still be insured; this informs the user that inboedel will be blocked. */}
            {isInboedelBlockedByRental && (
              <PropertyWarning
                id="rented-warning"
                titleKey="screens.woonhuis.rentedWarning.title"
                messageKey="screens.woonhuis.rentedWarning.message"
                contactInfo={contactInfo}
              />
            )}

            {/* Building specifications — shared single-source component */}
            <WoningSpecificaties
              variant="woonhuis"
              ba={data?.Contractdocument?.PP?.BA}
              styles={styles}
              onFieldChange={handleCustomFieldChange}
              lockedFields={infofolioFilledFields}
              errors={validationErrors}
              onNumberBlur={handleBuildingNumberBlur}
            />

          </div>

          {/* 1. Policy Details Section - Merged card for Policyholder, Insured Amount, and Deductible */}
          <div style={styles.addressContainer}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIconContainer}>
                <Users size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.iconOpaque} />
              </div>
              <h2 style={styles.addressTitle}>{t('screens.woonhuis.sections.policyDetails')}</h2>
            </div>

            {/* Policyholder fields - Responsive flex row */}
            <div className="woonhuis-address-row">
              {/* Family composition - Woonhuis is first in workflow, always show */}
                <div style={styles.addressField}>
                  <label style={styles.fieldLabel} htmlFor="gezin">
                    {t('screens.woonhuis.fields.familyComposition')} <span style={styles.required}>*</span>
                  </label>
                  <select
                    id="gezin"
                    value={familyComposition || data?.Contractdocument?.PP?.OP?.OP_GSS || readFamilyComposition(data) || ''}
                    onChange={(e) => {
                      handleCustomFieldChange('Contractdocument.PP.OP.OP_GSS', e.target.value);
                      setFamilyComposition(e.target.value);
                    }}
                    className={validationErrors['Contractdocument.PP.OP.OP_GSS'] ? 'field-error' : ''}
                    data-error={validationErrors['Contractdocument.PP.OP.OP_GSS'] ? 'true' : undefined}
                    style={{
                      ...styles.fieldSelect,
                      ...(validationErrors['Contractdocument.PP.OP.OP_GSS'] ? styles.fieldSelectError : {})
                    }}
                  >
                    <option value="">{t('screens.woonhuis.placeholders.select')}</option>
                    <option value="A">{t('screens.woonhuis.options.familyComposition.singleWithoutChildren')}</option>
                    <option value="H">{t('screens.woonhuis.options.familyComposition.coupleWithoutChildren')}</option>
                    <option value="I">{t('screens.woonhuis.options.familyComposition.coupleWithChildren')}</option>
                    <option value="K">{t('screens.woonhuis.options.familyComposition.singleWithChildren')}</option>
                  </select>
                  {validationErrors['Contractdocument.PP.OP.OP_GSS'] && (
                    <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.OP.OP_GSS']}</p>
                  )}
                </div>

              <div style={styles.addressField}>
                <label style={styles.fieldLabel} htmlFor="storm">
                  {t('screens.woonhuis.fields.stormDeductible')} <span style={styles.required}>*</span>
                </label>
                {/* Storm deductible shown as the amount itself: € 250 (OP_STORM 'N', default) or
                    € 0 (OP_STORM 'J' = afkoop). The wire value stays J/N; the amount is OP_ERBSTRM. */}
                <div style={styles.radioGroup} id="storm">
                  {STORM_DEDUCTIBLE_OPTIONS.map(({ value: optValue, amount }) => {
                    const selected = (data?.Contractdocument?.PP?.OP?.OP_STORM || '') === optValue;
                    const hasError = !selected && !!validationErrors['Contractdocument.PP.OP.OP_STORM'];
                    return (
                      <div
                        key={optValue}
                        style={selected ? styles.radioOptionSelected : (hasError ? styles.radioOptionError : styles.radioOption)}
                        onClick={() => {
                          handleCustomFieldChange('Contractdocument.PP.OP.OP_STORM', optValue);
                          setValidationErrors(prev => { const updated = { ...prev }; delete updated['Contractdocument.PP.OP.OP_STORM']; return updated; });
                        }}
                      >
                        <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                          {selected && <div style={styles.radioDotInner} />}
                        </div>
                        € {amount}
                      </div>
                    );
                  })}
                </div>
                {validationErrors['Contractdocument.PP.OP.OP_STORM'] && (
                  <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.OP.OP_STORM']}</p>
                )}
              </div>
            </div>

            {/* Herbouwwaarde + its bepaling share a row; each disappears when Infofolio filled it. */}
            {(!rebuildValueFromInfofolio || !rebuildValuationFromInfofolio) && (
            <div className="woonhuis-address-row">
              {!rebuildValueFromInfofolio && (
              <div style={{ ...styles.addressField, ...styles.flex1 }}>
                <label style={styles.fieldLabel} htmlFor="verzekerd-bedrag">
                  {t('screens.woonhuis.fields.insuredSum')} <span style={styles.required}>*</span>
                </label>
                <div style={{
                  ...styles.currencyInputContainer,
                  ...(infofolioFilledFields.has('verzsom') && !!data?.Contractdocument?.PP?.OP?.OP_VERZSOM ? styles.infofolioFilled : {})
                }}>
                  <span style={styles.currencySymbol}>€</span>
                  <input
                    id="verzekerd-bedrag"
                    type="text"
                    inputMode="numeric"
                    value={data?.Contractdocument?.PP?.OP?.OP_VERZSOM ? formatCurrency(data.Contractdocument.PP.OP.OP_VERZSOM) : ''}
                    readOnly={infofolioFilledFields.has('verzsom') && !!data?.Contractdocument?.PP?.OP?.OP_VERZSOM}
                    onChange={(e) => {
                      if (infofolioFilledFields.has('verzsom') && !!data?.Contractdocument?.PP?.OP?.OP_VERZSOM) return;
                      const value = unformatCurrency(e.target.value);
                      if (value !== null) {
                        handleCustomFieldChange('Contractdocument.PP.OP.OP_VERZSOM', value);
                        if (value) {
                          setValidationErrors(prev => { const updated = { ...prev }; delete updated['Contractdocument.PP.OP.OP_VERZSOM']; return updated; });
                        }
                      }
                    }}
                    className={validationErrors['Contractdocument.PP.OP.OP_VERZSOM'] ? 'field-error' : ''}
                    data-error={validationErrors['Contractdocument.PP.OP.OP_VERZSOM'] ? 'true' : undefined}
                    style={{
                      ...styles.currencyInput,
                      ...(validationErrors['Contractdocument.PP.OP.OP_VERZSOM'] ? styles.fieldInputError : {})
                    }}
                    placeholder={t('screens.woonhuis.placeholders.insuredSum')}
                  />
                </div>
                {validationErrors['Contractdocument.PP.OP.OP_VERZSOM'] && (
                  <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.OP.OP_VERZSOM']}</p>
                )}
              </div>
              )}

              {!rebuildValuationFromInfofolio && (
              <div style={styles.addressField}>
                <label style={styles.fieldLabel} htmlFor="herbvst">
                  {t('screens.woonhuis.fields.rebuildValueDetermination')} <span style={styles.required}>*</span>
                </label>
                <select
                  id="herbvst"
                  defaultValue={data?.Contractdocument?.PP?.BA?.BA_HERBVST || ''}
                  disabled={infofolioFilledFields.has('herbvst') && !!data?.Contractdocument?.PP?.BA?.BA_HERBVST}
                  onChange={(e) => {
                    handleCustomFieldChange('Contractdocument.PP.BA.BA_HERBVST', e.target.value);
                  }}
                  className={validationErrors['Contractdocument.PP.BA.BA_HERBVST'] ? 'field-error' : ''}
                  data-error={validationErrors['Contractdocument.PP.BA.BA_HERBVST'] ? 'true' : undefined}
                  style={{
                    ...styles.fieldSelect,
                    ...(validationErrors['Contractdocument.PP.BA.BA_HERBVST'] ? styles.fieldSelectError : {}),
                    ...(infofolioFilledFields.has('herbvst') && !!data?.Contractdocument?.PP?.BA?.BA_HERBVST ? styles.infofolioFilled : {})
                  }}
                >
                  <option value="">{t('screens.woonhuis.placeholders.select')}</option>
                  <option value="E">{t('screens.woonhuis.options.rebuildValue.ownerValuation')}</option>
                  {/* Maatschappij is system-set (Infofolio) only — rendered (disabled) solely
                      so a pre-set value displays; never user-pickable. */}
                  {data?.Contractdocument?.PP?.BA?.BA_HERBVST === 'M' && (
                    <option value="M" disabled>{t('screens.woonhuis.options.rebuildValue.companyValuation')}</option>
                  )}
                  <option value="V">{t('screens.woonhuis.options.rebuildValue.fixedValuation')}</option>
                </select>
                {validationErrors['Contractdocument.PP.BA.BA_HERBVST'] && (
                  <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.BA.BA_HERBVST']}</p>
                )}
              </div>
              )}
              {rebuildValueFromInfofolio !== rebuildValuationFromInfofolio && <div style={styles.addressField} aria-hidden="true" />}
            </div>
            )}

            {data?.Contractdocument?.PP?.BA?.BA_HERBVST === 'V' && (
              <div className="woonhuis-address-row">
                <div style={styles.addressField}>
                  <label style={styles.fieldLabel} htmlFor="eindgtx">
                    {t('screens.woonhuis.fields.valuationEndDate')} <span style={styles.required}>*</span>
                  </label>
                  <DatePicker
                    id="eindgtx"
                    hasError={!!validationErrors['Contractdocument.PP.BA.BA_EINDGTX']}
                    value={(() => {
                      const val = data?.Contractdocument?.PP?.BA?.BA_EINDGTX;
                      if (!val) return '';
                      const s = String(val);
                      if (s.length === 8) return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`;
                      return '';
                    })()}
                    onChange={(dateStr: string) => {
                      if (dateStr && dateStr.length === 10) {
                        const parts = dateStr.split('-');
                        const numValue = Number(`${parts[2]}${parts[1]}${parts[0]}`);
                        handleCustomFieldChange('Contractdocument.PP.BA.BA_EINDGTX', numValue);
                      } else if (!dateStr) {
                        handleCustomFieldChange('Contractdocument.PP.BA.BA_EINDGTX', null);
                      }
                    }}
                    futureOnly={true}
                    placeholder="DD-MM-JJJJ"
                  />
                  {validationErrors['Contractdocument.PP.BA.BA_EINDGTX'] && (
                    <p style={styles.errorMessage}>{validationErrors['Contractdocument.PP.BA.BA_EINDGTX']}</p>
                  )}
                </div>
                <div style={styles.addressField}>
                  <label style={styles.fieldLabel} htmlFor="taxvrt">
                    {t('screens.woonhuis.fields.vrtValuation')} <span style={styles.required}>*</span>
                  </label>
                  <div style={styles.radioGroup} id="taxvrt">
                    {(['J', 'N'] as const).map((optValue) => {
                      const selected = (data?.Supportsection?.Divers?.TaxatieVRT || '') === optValue;
                      const hasError = !selected && !!validationErrors['Supportsection.Divers.TaxatieVRT'];
                      return (
                        <div
                          key={optValue}
                          style={selected ? styles.radioOptionSelected : (hasError ? styles.radioOptionError : styles.radioOption)}
                          onClick={() => handleCustomFieldChange('Supportsection.Divers.TaxatieVRT', optValue)}
                        >
                          <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                            {selected && <div style={styles.radioDotInner} />}
                          </div>
                          {optValue === 'J' ? t('common.yes') : t('common.no')}
                        </div>
                      );
                    })}
                  </div>
                  {validationErrors['Supportsection.Divers.TaxatieVRT'] && (
                    <p style={styles.errorMessage}>{validationErrors['Supportsection.Divers.TaxatieVRT']}</p>
                  )}
                </div>
              </div>
            )}

            {/* OP_GARONDV (garantie tegen onderverzekering) is derived from BA_HERBVST and neither
                editable nor shown here. The All Risk box below carries the guarantee as product
                text regardless of the flag (product decision). */}
          </div>

          {/* 2. Coverage Options Section (Dekking opties) */}
          <div style={styles.addressContainer}>
            <div style={styles.sectionHeader} className="woonhuis-coverage-header">
              <div style={styles.rowCenterFlex}>
                <div style={{ ...styles.sectionIconContainer, ...styles.mr12 }}>
                  <Shield size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.iconOpaque} />
                </div>
                <h2 style={styles.addressTitle}>{t('screens.woonhuis.sections.coverage')}</h2>
              </div>
              {(() => {
                // UI-only ex-provision display premium. Sums section BTPs (OP discounted 7.5%, GP excluded),
                // strips provision, then adds 21% assurantiebelasting. Matches AVP's display formula.
                // Wait for config to resolve — otherwise the helper sees DEFAULT_CONFIG (no discount) on
                // first render for persisted pre-marker data and would briefly render the undiscounted value.
                const totalPremium = computeWoonhuisDisplayPremium(data, config as any);

                if (totalPremium > 0 && !isCalculating && !isConfigLoading) {
                  return (
                    <span className="woonhuis-total-badge" style={styles.totalBadge}>
                      {t('screens.woonhuis.sections.total')}: €{formatPrice(totalPremium)}
                    </span>
                  );
                }
                return (
                  <span className="woonhuis-total-badge" style={styles.totalBadge}>
                    {isCalculating || isConfigLoading ? (
                      <span className="price-spinner" />
                    ) : (
                      <>
                        <CurrencyEur size={20} weight="bold" />
                        <span>—</span>
                      </>
                    )}
                  </span>
                );
              })()}
            </div>

            {/* Hoofddekking - fixed to All Risk */}

            {/* Base Coverage label with info icon and price badge (All Risk + Glass included) */}
            {(() => {
              // Same UI-only ex-provision helper as the section total badge — All Risk covers OP + GP.
              const basePremium = computeWoonhuisDisplayPremium(data, config as any);
              const dekkingLabel = 'Woonhuis All Risk';
              const stormWaived = data?.Contractdocument?.PP?.OP?.OP_STORM === 'J';
              const stormAmount = stormWaived ? 0 : (data?.Contractdocument?.PP?.OP?.OP_ERBSTRM ?? 250);

              return (
                <>
                  {/* Tinted block containing the title row + the all-risk bullets,
                      matching the Inboedel "Dekking & premie" card (shared baseCoverage* styles). */}
                  <div style={styles.baseCoverageBox}>
                    <div style={styles.rowBetweenFull}>
                      <label style={styles.baseCoverageTitle}>
                        <span style={styles.rowCenter8}>
                          {dekkingLabel}
                        </span>
                      </label>
                      {basePremium > 0 && !isConfigLoading && (
                        <span style={styles.baseCoverageBadge}>
                          €{formatPrice(basePremium)}
                        </span>
                      )}
                    </div>
                    <ul style={styles.baseCoverageList}>
                      {[1, 2, 3, 4].map((n) => (
                        <li key={n} style={styles.baseCoverageBullet}>
                          <Check size={16} color="var(--primary-color, #056666)" style={styles.iconNudge} />
                          <span>{t(`screens.woonhuis.coverageInfo.homeAllrisk.item${n}`)}</span>
                          {n === 1 && (
                            <InfoTooltip
                              title={t('screens.woonhuis.coverageInfo.underinsuranceGuarantee.title')}
                              text={t('screens.woonhuis.coverageInfo.underinsuranceGuarantee.text')}
                            />
                          )}
                        </li>
                      ))}
                      {/* Mirrors the storm-deductible choice above (€ 0 or € 250), always a check. */}
                      <li style={styles.baseCoverageBullet}>
                        <Check size={16} color="var(--primary-color, #056666)" style={styles.iconNudge} />
                        <span>{t('screens.woonhuis.coverageInfo.stormDeductible', { amount: stormAmount })}</span>
                      </li>
                    </ul>
                  </div>
                </>
              );
            })()}
          </div>
            </div> {/* End formColumn */}

            <WorkflowNavButtons
              className="woonhuis-navigation"
              onBack={handlePrevious}
              onNext={handleNext}
              nextLabel={isCalculating ? 'common.calculating' : isMonumentProperty ? 'screens.woonhuis.uninsurableMonument' : 'common.nextPlain'}
              nextDisabled={isCalculating || isMonumentProperty}
              nextLoading={isCalculating}
              center={
                <div style={styles.productCounter}>
                  {t('common.productCounter', { current: currentProductIndex, total: totalProducts })}
                </div>
              }
            />
          </WithHelpSidebar>

          {/* Debug panel for input/output (collapsible) - Only in local environment */}
          <DebugPanel
            input={lastCalculationInput}
            output={lastCalculationOutput}
            onRecalculate={handleManualCalculate}
            calculating={isCalculating}
            calcDisabled={isCalculating || isMonumentProperty}
            calcLabel={isMonumentProperty ? t('screens.woonhuis.uninsurableMonument') : undefined}
            validationErrors={validationErrors}
          />

        </div>
      </main>
    </WorkflowPageLayout>
  );
};


export default WoonhuisScreen;
